import moment from "moment/moment";
import { fetchWithAuth } from "../utils/fetch/FetchWithAuth";
import { prepareBaseGetListParams } from "../utils/fetch/PrepareBaseGetListParams";
import { pickFields } from "../utils/pickFields";
import { Constants } from "../utils/Constants";
import { prepareEnumSearchParams } from "../utils/fetch/PrepareEnumSearchParams";


export const DeviceService = {

    _prepareDeviceData: device => {
        const data = pickFields(
            device,
            {
                id: true,
                externalSystemId: true,
                name: true,

                deviceType: {
                    id: true,
                },

                supportEveDts: true,

                terminal: {
                    id: true,
                },

                location: {
                    id: true,
                },

                rows: true,
                cols: true,

                planogram: {
                    id: true,
                },

                configurationProfileId: true,

                serialNumberDeviceHousing: true,

                manufacturer: {
                    id: true,
                    name: true,
                },

                model: true,
                comments: true,
                remotePriceUpdate: true,
                generateQrCode: true,
                planogramImplementationDate: true,
            },
        );

        data.status =
            device.status?.id;

        data.paymentMethods =
            device.paymentMethods?.map(
                paymentMethod =>
                    paymentMethod.id,
            );

        return data;
    },

    getList: (criteria, enums) => {
        console.log('DeviceService::getList-criteria', criteria);
        const params = prepareBaseGetListParams(criteria);
        prepareEnumSearchParams(criteria.baseQuery, params, enums);
        params.append('only_active', criteria.showOnlyActive || false);
        if (criteria.healthState) {
            params.append('health_states', criteria.healthState);
        }
        if (criteria.groupId) {
            params.append('group_id', criteria.groupId);
        }
        if (criteria.productId) {
            params.append('product_id', criteria.productId);
        }
        if (criteria.price) {
            params.append('product_price', criteria.price);
        }
        if (criteria.status) {
            params.append('statuses', [criteria.status]);
        }
        if (criteria.showInaccessible) {
            params.append('show_inaccessible', criteria.showInaccessible);
        }
        const url = `/portal/devices?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },

    getListAtLocation: (criteria, locationId) => {
        console.log('DeviceService::getListAtLocation-criteria', criteria);
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/devices/location/${locationId}?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },

    getListAtPlanogram: (criteria, planogramId) => {
        console.log('DeviceService::getListAtPlanogram-criteria', criteria);
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/devices/planogram/${planogramId}?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },

    deleteDeviceListFromPlanogram: (criteria, planogramId, selectedAll, exceptionIds) => {
        console.log('DeviceService::deleteDeviceListFromPlanogram-criteria', criteria);
        const params = prepareBaseGetListParams(criteria);
        return fetchWithAuth(`/portal/devices/planogram/${planogramId}?${params.toString()}`, { method: 'DELETE', body: JSON.stringify({selectedAll, exceptions: exceptionIds}) });

    },

    getDeviceTypeList: () => {
        return fetchWithAuth('/portal/devices/device-type-list', { method: 'GET' });
    },

    getStatusList: () => {
        return fetchWithAuth('/portal/devices/status-list', { method: 'GET' });
    },
    
    getHealthStateList: () => {
        return fetchWithAuth('/portal/devices/health-state-list', { method: 'GET' });
    },
    
    getDeviceNamesAtLocation: (locationId) => {
        return fetchWithAuth(`/portal/devices/location/${locationId}/names`, { method: 'GET' });
    }, 

    getEligibleDeviceNamesAtLocation: (locationId) => {
        return fetchWithAuth(`/portal/devices/location/${locationId}/eligible-devices/names`, { method: 'GET' });
    },

    getDeviceNamesAtPlanogram: (planogramId) => {
        return fetchWithAuth(`/portal/devices/planogram/${planogramId}/names`, { method: 'GET' });
    },

    getUnassignedDevicesNames: () => {
        return fetchWithAuth('/portal/devices/unassigned/names', { method: 'GET' });
    },

    addDevicesToLocation: (locationId, devices) => {
        return fetchWithAuth(`/portal/devices/location/${locationId}/add`, { method: 'POST', body: JSON.stringify(devices.map(d => d.id)) });
    },
    deleteDeviceListFromLocation: (criteria, locationId, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        return fetchWithAuth(`/portal/devices/location/${locationId}/list?${params.toString()}`, { method: 'DELETE', body: JSON.stringify({selectedAll, exceptions: exceptionIds}) });
    },    
    create: (device) => {
        console.log('DeviceService::create-device', device);
        const d = DeviceService._prepareDeviceData(device);
        return fetchWithAuth('/portal/devices', { method: 'POST', body: JSON.stringify(d) });
    },
    duplicate: (sourceDeviceId, request) => {
        return fetchWithAuth(`/portal/devices/${sourceDeviceId}/duplicate`, {
            method: 'POST',
            body: JSON.stringify({
                name: request.name,
                locationId: request.locationId ?? null,
                terminalId: request.terminalId ?? null,
            }),
        });
    },
    update: (device) => {
        console.log('DeviceService::update-device', device);
        const d = DeviceService._prepareDeviceData(device);
        return fetchWithAuth(`/portal/devices/${d.id}`, { method: 'PUT', body: JSON.stringify(d) });
    },
    get: (id) => {
        return fetchWithAuth(`/portal/devices/${id}`, { method: 'GET' });
    },
    
    deactivate: (id) => {
        return fetchWithAuth(`/portal/devices/${id}/deactivate`, { method: 'PUT' });
    },
    
    activate: (id) => {
        return fetchWithAuth(`/portal/devices/${id}/activate`, { method: 'PUT' });
    },
    
    delete: (id) => {
        return fetchWithAuth(`/portal/devices/${id}`, { method: 'DELETE' });
    },
    deleteList: (criteria, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        if (criteria.healthState) {
            params.append('health_states', criteria.healthState);
        }
        return fetchWithAuth(`/portal/devices/list?${params.toString()}`, { method: 'DELETE', body: JSON.stringify({selectedAll, exceptions: exceptionIds})});
    },
    getSelectedDevices: (criteria, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        if (criteria.healthState) {
            params.append('health_states', criteria.healthState);
        }
        return fetchWithAuth(`/portal/devices/selected?${params.toString()}`, { method: 'POST', body: JSON.stringify({selectedAll, exceptions: exceptionIds}) });
    },
    removeFromGroup: (groupId, criteria, selectedAll, exceptionIds) => {
        const params = prepareBaseGetListParams(criteria, false);
        return fetchWithAuth(`/portal/devices/group/${groupId}/remove-devices?${params.toString()}`, { method: 'DELETE', body: JSON.stringify({selectedAll, exceptions: exceptionIds}) });
    },
    getDevicesMetric: (healthState) => {
        let url = '/portal/devices/metric';
        if (healthState) {
            url += `?health_state=${healthState}`;
        }
        return fetchWithAuth(url, { method: 'GET' });
    },
    getAssignedDevices: () => {
        return fetchWithAuth('/portal/devices/assigned-devices', { method: 'GET' });
    },
    // Fetches devices for simple planogram view for a parent planogram id
    getSimpleListDeviceAtPlanogram: (planogramId) => {
        const url = `/portal/devices/simple-planogram/${planogramId}`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    listDeviceAtLocationsNames: (locationIds) => {
        const filteredIds = (locationIds || []).filter(Boolean);
        if (filteredIds.length === 0) {
            return Promise.resolve([]);
        }
        const params = new URLSearchParams();
        filteredIds.forEach(id => params.append('location_id', id));
        return fetchWithAuth(`/portal/devices/locations/batch/names?${params.toString()}`, { method: 'GET' });
    },
    getAllDevices: () => {
        return fetchWithAuth('/portal/devices/list-all-devices', { method: 'GET' });
    },
    getDevicesForCashOperations: () => {
        return fetchWithAuth('/portal/devices/devices-for-cash-operations', { method: 'GET' });
    },
    getActiveDeviceCount: (startDate, endDate) => {
        const params = new URLSearchParams();
        const formatDate = ((value) => moment(value).utc().format(Constants.DATE_TIME_FORMAT_GET));
        params.append('start_date', formatDate(startDate));
        params.append('end_date', formatDate(endDate));
        const url = `/portal/devices/active-count?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    getSlotStatus: (deviceId, criteria) => {
        const params = prepareBaseGetListParams(criteria);
        const url = `/portal/devices/${deviceId}/slot-status?${params.toString()}`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    getSlotStatusList: () => {
        return fetchWithAuth('/portal/devices/slot-status-list', { method: 'GET' });
    },
    updateSlotStatusQuantity: (cellExternalId, quantity) => {
        console.log('DeviceService.updateSlotStatusQuantity - cellExternalId:', cellExternalId, 'quantity:', quantity);
        const url = `/portal/devices/slot-status/${cellExternalId}/quantity`;
        const body = JSON.stringify({ quantity });
        console.log('DeviceService.updateSlotStatusQuantity - URL:', url, 'Body:', body);
        return fetchWithAuth(url, { method: 'PATCH', body });
    },
    setSlotStatusQuantityToMax: (deviceId, criteria) => {
        console.log('DeviceService.setSlotStatusQuantityToMax - deviceId:', deviceId, 'criteria:', criteria);
        const params = prepareBaseGetListParams(criteria, false); // false = bez paginacji
        const url = `/portal/devices/${deviceId}/slot-status/set-quantity-to-max?${params.toString()}`;
        console.log('DeviceService.setSlotStatusQuantityToMax - URL:', url);
        return fetchWithAuth(url, { method: 'PATCH' });
    },
    updateConfigurationGuideVisibility: (deviceId, visible) => {
        return fetchWithAuth(
            `/portal/devices/${deviceId}/configuration-guide`,
            {
                method: 'PUT',
                body: JSON.stringify({ visible }),
            },
        );
    },

    getDeviceManufacturerList: (search = '') => {
        const params = new URLSearchParams();

        const normalizedSearch = search.trim();

        if (normalizedSearch) {
            params.set('search', normalizedSearch);
        }

        const query = params.toString();

        return fetchWithAuth(
            `/portal/dictionary/device-manufacturer${
                query ? `?${query}` : ''
            }`,
            {
                method: 'GET',
            },
        );
    },

    createDeviceManufacturer: (name) => {
        return fetchWithAuth(
            '/portal/dictionary/device-manufacturer',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: name.trim(),
                }),
            },
        );
    },
}
